import { Typography } from "@mui/material"
import type { FC } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { twMerge } from "tailwind-merge"

import type { SalesOrder } from "@/graphql/codegen/graphql"
import type { SalesOrderLogisticsFormValues } from "@/screens/CustomerOrders/schemas"
import { formatCurrency } from "@/utils/currency"
import { formatNumber } from "@/utils/number"

interface CountBoxProps {
  label: string
  value: string | null
  className?: string
  "data-testid": string
}

const CountBox: FC<CountBoxProps> = ({ label, value, className, "data-testid": testId }) => (
  <div
    className={twMerge("flex h-16 min-w-24 flex-col justify-center rounded-md bg-gray-200 px-4", className)}
    data-testid={testId}
  >
    <Typography variant='caption' component='span' className='text-primary'>
      {label}
    </Typography>
    <Typography variant='h6' component='span' className='font-bold text-gray-700'>
      {value ?? "—"}
    </Typography>
  </div>
)

interface SalesOrderTotalsProps {
  salesOrder: SalesOrder
}

// TODO(AK-6206): Rework the totals row, including the empty weight boxes, in scope of that ticket.
export const SalesOrderTotals: FC<SalesOrderTotalsProps> = ({ salesOrder }) => {
  const { control } = useFormContext<SalesOrderLogisticsFormValues>()
  // Watched, not salesOrder.lines — so this stays in sync with an in-progress edit to any
  // line's Units field, matching each row's own live "Total:" caption.
  const lines = useWatch({ control, name: "lines" })
  const totalUnits = lines.reduce((sum, line) => sum + (line.units ?? 0), 0)

  return (
    <div className='flex items-center justify-end gap-3' data-testid='customer-orders.so-line-items.totals-section'>
      <Typography variant='body2' className='mr-2 text-gray-700'>
        SO#: {salesOrder.publicId} Total
      </Typography>
      <CountBox
        label='Pallets'
        value={formatNumber(salesOrder.totalPallets, 0, 0)}
        data-testid='customer-orders.so-line-items.totals-pallets'
      />
      {/* Short Tons/Pounds/Tons/Kilograms need each line's product package-weight ratios to convert correctly — not available on a Sales Order line yet, so these stay empty until that data lands. */}
      <CountBox label='Short Tons (st)' value={null} data-testid='customer-orders.so-line-items.totals-short-tons' />
      <CountBox label='Pounds (lb)' value={null} data-testid='customer-orders.so-line-items.totals-pounds' />
      <CountBox label='Tons (mt)' value={null} data-testid='customer-orders.so-line-items.totals-tons' />
      <CountBox label='Kilograms (kg)' value={null} data-testid='customer-orders.so-line-items.totals-kilograms' />
      <CountBox
        label='Units'
        value={formatNumber(totalUnits, 0, 0)}
        data-testid='customer-orders.so-line-items.totals-units'
      />
      <CountBox
        label='Amount'
        value={formatCurrency(salesOrder.totalAmount)}
        data-testid='customer-orders.so-line-items.totals-amount'
      />
    </div>
  )
}
